import { useState, useEffect } from 'react';

/* ========================================================================= */
/* Side effects */
/* ========================================================================= */

/**
 * @hook useLazyEffect
 * @param {function} callback
 * @params {any} args
 * @return {boolean}
 * 
 * Implements a lazy version of React’s useEffect (skiping the first trigger on page load).
 */
export const useLazyEffect = (callback = () => {}, ...args) => {
    const [ isExecuted, setIsExecuted ] = useState(false);
    useEffect((...effectArgs) => {
        if (!isExecuted) {
            return;
        }

        const cleanup = callback(...effectArgs);
        return typeof cleanup === 'function' ? cleanup : undefined;
    }, ...args);
    useEffect(() => setIsExecuted(true), []);

    return isExecuted;
};

/**
 * @hook useLazyRender
 * @return {boolean}
 * 
 * Implements a lazy render boolean (eg.: useful when using useLayoutEffect).
 */
export const useLazyRender = () => {
    const [ isLazyRenderReady, setIsLazyRenderReady ] = useState(false);
    useEffect(() => setIsLazyRenderReady(true), []);

    return isLazyRenderReady;
};

/**
 * @hook useAsync
 * @param {function} callback - Function returning the asynchronous data.
 * @param {array} depedencies (optional)
 * @return {any}
 * 
 * Provides a wrapping hook for any asynchronous function fetching data.
 */
export const useAsync = (callback, depedencies = []) => {
    const [ state, setState ] = useState(null);

    useEffect(() => {
        (async () => setState(await callback()))();
    }, depedencies);

    return state;
};